import React, { useState, useEffect } from "react";
import MDEditor from "@uiw/react-md-editor";
import rehypeSanitize from "rehype-sanitize";
import { Search, Save, FileText, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, getValidToken } from "../../apiClient";

const Notes = () => {
  const queryClient = useQueryClient();

  // --- State ---
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNote, setSelectedNote] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Editor State
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  // --- API Hooks ---
  // 1. Fetch Notes
  const { data: notes = [], isLoading } = useQuery({
    queryKey: ["standalone-notes"],
    queryFn: async () => {
      const { userid } = await getValidToken();
      const data = await apiClient(`/problems/getnotes/${userid}`);
      
      // Filter out Cloudinary image notes
      return data.totalnote.filter(n => !n.title?.startsWith("image_"));
    },
  });

  // 2. Add Note
  const { mutate: addNote, isPending: isAdding } = useMutation({
    mutationFn: (newNote) => apiClient("/problems/addnote", {
      method: "POST",
      body: JSON.stringify({ notes: [newNote] }),
    }),
    onSuccess: (data) => {
      toast.success("Note created!");
      queryClient.invalidateQueries(["standalone-notes"]);
      setIsCreating(false);
      // Auto-select the newly created note (assuming it's the last one in the returned array)
      const newNotesList = data.totalnote.filter(n => !n.title?.startsWith("image_"));
      setSelectedNote(newNotesList[newNotesList.length - 1]);
    },
    onError: (err) => toast.error(err.message),
  });

  // 3. Edit Note
  const { mutate: editNote, isPending: isEditing } = useMutation({
    mutationFn: (updatedNote) => apiClient("/problems/editnote", {
      method: "PUT",
      body: JSON.stringify(updatedNote),
    }),
    onSuccess: () => {
      toast.success("Note updated!");
      queryClient.invalidateQueries(["standalone-notes"]);
    },
    onError: (err) => toast.error(err.message),
  });

  // 4. Delete Note
  const { mutate: deleteNote } = useMutation({
    mutationFn: (id) => apiClient("/problems/deletenote", {
      method: "DELETE",
      body: JSON.stringify({ _id: id }),
    }),
    onSuccess: () => {
      toast.success("Note deleted");
      queryClient.invalidateQueries(["standalone-notes"]);
      setSelectedNote(null);
    },
    onError: (err) => toast.error(err.message),
  });

  // --- Handlers ---
  const filteredNotes = notes.filter((n) =>
    n.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sync editor when selection changes
  useEffect(() => {
    if (selectedNote && !isCreating) {
      setEditTitle(selectedNote.title || "");
      
      // Clean up old <p> tags from the legacy ReactQuill editor
      let cleanContent = selectedNote.note || "";
      if (cleanContent.startsWith("<p>") && cleanContent.endsWith("</p>")) {
        cleanContent = cleanContent.substring(3, cleanContent.length - 4);
      }
      setEditContent(cleanContent);
    }
  }, [selectedNote, isCreating]);

  const handleStartCreate = () => {
    setSelectedNote(null);
    setIsCreating(true);
    setEditTitle("");
    setEditContent("");
  };

  const handleSave = () => {
    if (!editTitle.trim()) {
      toast.error("Please provide a title");
      return;
    }

    if (isCreating) {
      addNote({ title: editTitle, note: editContent });
    } else if (selectedNote) {
      editNote({ _id: selectedNote._id, title: editTitle, note: editContent });
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this note?")) {
      deleteNote(selectedNote._id);
    }
  };

  // --- Render ---
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto h-[80vh] flex items-center justify-center text-slate-400 animate-pulse">
        Loading your notes...
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-8rem)] flex flex-col md:flex-row gap-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-2">
      
      {/* LEFT SIDEBAR: Notes List */}
      <div className="w-full md:w-80 flex flex-col border-r border-slate-100 bg-slate-50/30 rounded-xl overflow-hidden shrink-0">
        <div className="p-4 border-b border-slate-100 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              My Notes
            </h2>
            <button 
              onClick={handleStartCreate}
              className="p-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors"
              title="Create New Note"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {filteredNotes.length === 0 ? (
            <div className="text-center text-sm text-slate-500 py-8">
              No notes found.
            </div>
          ) : (
            filteredNotes.map((note) => (
              <button
                key={note._id}
                onClick={() => {
                  setIsCreating(false);
                  setSelectedNote(note);
                }}
                className={`w-full text-left px-3 py-3 rounded-lg text-sm transition-all ${
                  selectedNote?._id === note._id && !isCreating
                    ? "bg-blue-50 text-blue-700 font-semibold border border-blue-100 shadow-sm"
                    : "text-slate-600 hover:bg-slate-100 border border-transparent"
                }`}
              >
                <span className="line-clamp-1">{note.title}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* RIGHT SIDE: Markdown Editor */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        {selectedNote || isCreating ? (
          <>
            {/* Editor Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between bg-white gap-4">
              <input 
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Note Title..."
                className="text-xl font-bold text-slate-800 focus:outline-none placeholder:text-slate-300 w-full sm:max-w-md bg-transparent"
              />
              
              <div className="flex items-center gap-2">
                {!isCreating && selectedNote && (
                  <button
                    onClick={handleDelete}
                    className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                    title="Delete Note"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={isAdding || isEditing}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-sm"
                >
                  <Save className="w-4 h-4" />
                  {isAdding || isEditing ? "Saving..." : "Save Note"}
                </button>
              </div>
            </div>

            {/* Editor Body */}
            <div className="flex-1 p-6 overflow-hidden flex flex-col" data-color-mode="light">
              <MDEditor
                value={editContent}
                onChange={(val) => setEditContent(val || "")}
                previewOptions={{ rehypePlugins: [[rehypeSanitize]] }}
                className="flex-1 !border-none !shadow-none"
                textareaProps={{
                  placeholder: "Write your Markdown notes here..."
                }}
                height="100%"
              />
            </div>
          </>
        ) : (
          /* Empty State */
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-6 text-center">
            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mb-4 border border-slate-100">
              <FileText className="w-8 h-8 text-slate-300" />
            </div>
            <p className="text-lg font-medium text-slate-600">Select a note or create a new one</p>
            <p className="text-sm mt-1 text-slate-400 max-w-sm">
              Use Markdown to format your text, write code blocks, and organize your DSA knowledge.
            </p>
            <button 
              onClick={handleStartCreate}
              className="mt-6 px-6 py-2.5 bg-blue-50 text-blue-600 font-medium rounded-xl hover:bg-blue-100 transition-colors"
            >
              Create New Note
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notes;